import { isSkipped } from './contentRoot';
import { visibleText } from './textBlocks';
import { StyleView } from './types';

const MAX_LABEL = 120;
const MAX_PRECEDING_TEXT = 80;
const PRECEDING_TEXT_DEPTH = 3;

// Anything that is (or holds) a control belongs to another field and can't serve as a label.
const FIELD_SELECTOR = 'input,select,textarea,button,[role="button"],[role="combobox"],[role="tab"]';

// MUI v5+ renders the Select's visible element as `role="combobox"` (v4 used `role="button"`).
// An Autocomplete's `<input role="combobox">` is a regular text field, not a Select.
const SELECT_ROLES = new Set(['combobox', 'button']);

export const isMuiSelect = (el: Element): boolean => (
  el.tagName !== 'INPUT'
  && SELECT_ROLES.has(el.getAttribute('role') ?? '')
  && el.getAttribute('aria-haspopup') === 'listbox'
);

// MUI Selects keep `name` on their hidden native input, next to the visible button.
export const controlName = (el: Element): string | null => {
  if (!isMuiSelect(el)) return el.getAttribute('name');
  return el.parentElement?.querySelector('input.MuiSelect-nativeInput')?.getAttribute('name') ?? null;
};

const clean = (text: string | null | undefined): string => {
  const normalized = (text ?? '').replace(/\s+/g, ' ').replace(/\s*\*$/, '').trim();
  return normalized.length > MAX_LABEL ? `${normalized.slice(0, MAX_LABEL)}…` : normalized;
};

// A Select's `aria-labelledby` also names the Select itself, which would turn its value into its label.
const labelledByText = (el: Element, view: StyleView): string => {
  const ids = (el.getAttribute('aria-labelledby') ?? '').split(/\s+/).filter(Boolean);
  const doc = el.ownerDocument;
  return ids
    .map((id) => doc.getElementById(id))
    .filter((ref): ref is HTMLElement => ref !== null && !(isMuiSelect(el) && el.contains(ref)))
    .map((ref) => visibleText(ref, view))
    .join(' ');
};

const labelForText = (el: Element, view: StyleView): string => {
  // Labelable elements (input, textarea, select) expose their labels directly; only custom controls need a scan.
  const { labels } = el as HTMLInputElement;
  if (labels) return labels.length > 0 ? visibleText(labels[0], view) : '';
  if (!el.id) return '';
  const label = Array.from(el.ownerDocument.querySelectorAll('label'))
    .find((candidate) => candidate.htmlFor === el.id);
  return label ? visibleText(label, view) : '';
};

const wrappingLabelText = (el: Element, view: StyleView): string => {
  const label = el.closest('label');
  return label ? visibleText(label, view) : '';
};

// The shared TextField passes no `id`, so MUI emits no `for`; the label is only reachable through the FormControl.
const formControlLabelText = (el: Element, view: StyleView): string => {
  const formControl = el.closest('.MuiFormControl-root');
  if (!formControl) return '';
  const label = Array.from(formControl.querySelectorAll('label.MuiInputLabel-root, legend.MuiFormLabel-root'))
    .find((candidate) => candidate.closest('.MuiFormControl-root') === formControl);
  return label ? visibleText(label, view) : '';
};

const siblingText = (node: Element | null, view: StyleView): string => {
  if (!node) return '';
  if (isSkipped(node, view) || node.matches(FIELD_SELECTOR) || node.querySelector(FIELD_SELECTOR)) return '';
  const text = visibleText(node, view);
  if (text !== '' && text.length <= MAX_PRECEDING_TEXT) return text;
  return siblingText(node.previousElementSibling, view);
};

// Label-less DropDowns sit next to a caption (e.g. a Typography above the Select); look a few levels up for it.
export const precedingText = (el: Element, view: StyleView, depth = 0): string => {
  if (depth > PRECEDING_TEXT_DEPTH || !el.parentElement) return '';
  const text = siblingText(el.previousElementSibling, view);
  if (text !== '') return text;
  return precedingText(el.parentElement, view, depth + 1);
};

/**
 * Readable name of a control, in a fixed resolution order. Never falls back to element ids,
 * which in MUI are random (`mui-<n>`) and meaningless to the assistant.
 */
export const describeControl = (el: Element, view: StyleView): string => {
  const resolvers: Array<() => string | null> = [
    () => el.getAttribute('aria-label'),
    () => labelledByText(el, view),
    () => labelForText(el, view),
    () => wrappingLabelText(el, view),
    () => formControlLabelText(el, view),
    () => (isMuiSelect(el) ? precedingText(el.closest('.MuiInputBase-root') ?? el, view) : ''),
    () => el.getAttribute('placeholder'),
    () => controlName(el),
  ];

  const label = resolvers.reduce((found, resolve) => found || clean(resolve()), '');
  return label || '(unlabeled)';
};
